import { Button } from '@components/ui/button'
import { useMutation } from '@connectrpc/connect-query'
import { parseErrorMessage } from '@lib/errors'
import React, {
  Dispatch,
  FC,
  SetStateAction,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  type Crop,
} from 'react-image-crop'
import { toast } from 'sonner'
import {
  getPresignedUploadURL,
  updateMyProfilePicture,
} from '@gen/api/v1/api-ApiService_connectquery'

interface CropperProps {
  fileType: string
  setOpen: Dispatch<SetStateAction<boolean>>
  setProfilePictureUrl: Dispatch<SetStateAction<string>>
  url: string
}

const Cropper: FC<CropperProps> = ({
  fileType,
  setOpen,
  setProfilePictureUrl,
  url,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  const getPresignedUploadUrlMutation = useMutation(getPresignedUploadURL)
  const updateMyProfilePictureMutation = useMutation(updateMyProfilePicture)

  const [completedCrop, setCompletedCrop] = useState<Crop>()
  const [crop, setCrop] = useState<Crop>()
  const [uploadedUrl, setUploadedUrl] = useState<string>()

  const getCroppedImage = useCallback(() => {
    if (!completedCrop || !imageRef.current || !canvasRef.current) return

    const image = imageRef.current
    const canvas = canvasRef.current
    const crop = completedCrop

    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    const ctx = canvas.getContext('2d')

    if (!ctx) return

    canvas.width = crop.width
    canvas.height = crop.height

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height,
    )
  }, [completedCrop])

  const handleImageLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth: width, naturalHeight: height } = e.currentTarget

    const crop = centerCrop(
      makeAspectCrop(
        {
          // You don't need to pass a complete crop into
          // makeAspectCrop or centerCrop.
          unit: '%',
          width: 100,
        },
        1 / 1,
        width,
        height,
      ),
      width,
      height,
    )

    setCrop(crop)
  }

  const handleUpdate = async () => {
    try {
      const uploadUrlRes = await getPresignedUploadUrlMutation.mutateAsync({})

      if (!canvasRef.current) return

      canvasRef.current.toBlob(async (blob) => {
        if (!blob) return

        try {
          const response = await fetch(uploadUrlRes.url, {
            method: 'PUT',
            body: blob,
            headers: {
              'Content-Type': fileType,
            },
          })

          if (!response.ok) {
            toast.error('Failed to update profile picture')
            return
          }

          const url = new URL(uploadUrlRes.url)
          const profilePictureUrl = `${url.origin}${url.pathname}`

          await updateMyProfilePictureMutation.mutateAsync({
            url: profilePictureUrl,
          })

          setOpen(false)
          setProfilePictureUrl(profilePictureUrl)
        } catch (error) {
          console.error('Upload error:', error)
        }
      })
    } catch (error) {
      const message = parseErrorMessage(error)
      toast.error('Failed to update profile picture', {
        description: message,
      })
    }
  }

  useEffect(() => {
    getCroppedImage()
  }, [completedCrop])

  return (
    <>
      <canvas className="hidden" ref={canvasRef} />

      <ReactCrop
        aspect={1}
        crop={crop}
        onChange={setCrop}
        onComplete={setCompletedCrop}
      >
        <img onLoad={handleImageLoad} ref={imageRef} src={url} />
      </ReactCrop>

      <Button className="mt-8" onClick={handleUpdate}>
        Save
      </Button>
    </>
  )
}

export default Cropper
